import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { categories } from '../data/categories';
import { getProduct } from '../data/products';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
const goals = ['Energy, mood and sleep', 'Body composition or weight', 'Recovery from training or injury', 'Longevity and prevention'];
export function GetStarted() {
  const [params] = useSearchParams();
  const requested = getProduct(params.get('product') ?? '');
  const {
    brand,
    text
  } = useBrand();
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<string[]>(requested ? [requested.category] : []);
  const [goal, setGoal] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  useDocumentMeta({
    title: `Get started — ${brand.name}`,
    description: 'Three questions to start. Nothing is charged until a doctor reads your labs and you say yes to the plan.'
  });
  const total = 3;
  const canAdvance = step === 0 ? selected.length > 0 : step === 1 ? Boolean(goal) : email.includes('@');
  const toggle = (slug: string) => setSelected(current => current.includes(slug) ? current.filter(item => item !== slug) : [...current, slug]);
  if (submitted) {
    return <main className="bg-cream" data-id="element-340" data-mp="3pix4m6tmxytr">
        <div className="mx-auto max-w-shell px-6 py-24 md:px-8 md:py-32" data-id="element-341" data-mp="850bxma7l2xk">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-2xl" data-id="element-342" data-mp="86biftv1gosd">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brick text-cream" data-id="element-343" data-mp="36v0jl7vs8de2">
              <CheckIcon className="h-5 w-5" strokeWidth={2.25} data-id="element-344" data-mp="1aqawu0ejaa54" />
            </span>
            <h1 className="mt-8 font-serif text-4xl font-bold leading-tight tracking-tight text-ink md:text-5xl" data-id="element-345" data-mp="1fpx844asxgba">
              That is all we need for now.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-346" data-mp="1oir4jeidqwcd">
              A doctor licensed in your state will look this over and send your lab order to{' '}
              <span className="text-ink" data-id="element-347" data-mp="rucf8dj3fucc">{email}</span> within one business day. Nothing gets
              charged until you have seen a plan and said yes to it.
            </p>
            <Button to="/shop" className="mt-8" data-id="element-348" data-mp="10s4zlw3o9s6l">
              Keep looking around
              <ArrowRightIcon className="h-4 w-4" data-id="element-349" data-mp="15sj4j59z8m0e" />
            </Button>
          </motion.div>
        </div>
      </main>;
  }
  return <main className="bg-cream" data-id="element-350" data-mp="2c83ckb1z0yd3">
      <div className="mx-auto max-w-shell px-6 py-14 md:px-8 md:py-20" data-id="element-351" data-mp="2h7fzswwr87g7">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-352" data-mp="2h9ixm345f4vh">
          <div data-id="element-353" data-mp="2ra1se5bhpkcr">
            <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-354" data-mp="2ral619u3xmkz">Get started</p>
            <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-ink md:text-5xl" data-id="element-355" data-mp="2md1tqfgyzjvo">
              Three questions. That is it for now.
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-soft" data-id="element-356" data-mp="2dj6oz8jrfvgk">
              {text('About a minute of your time. The longer medical history only comes later, once a doctor confirms Republic is the right place for what you are dealing with.')}
            </p>

            {requested && <div className="mt-8 rounded-brand border border-ink/10 bg-cream-light p-5" data-id="element-357" data-mp="1ul3tcawpj09s">
                <p className="text-[0.625rem] uppercase tracking-label text-ink-muted" data-id="element-358" data-mp="1ungjvcgj6iyg">
                  You asked about
                </p>
                <p className="mt-2 font-serif text-lg font-semibold text-ink" data-id="element-359" data-mp="3oyvcq24u8yif">{requested.name}</p>
                <p className="mt-1 text-sm text-ink-muted" data-id="element-360" data-mp="3w21vwt3tniru">
                  ${requested.price}
                  {requested.cadence} · {requested.form}
                </p>
                <Link to={`/shop/${requested.slug}`} className="mt-3 inline-block text-sm text-brick transition-colors hover:text-ink" data-id="element-361" data-mp="3n1wz1cw982rx">
                  Read the details again
                </Link>
              </div>}

            <div className="rule mt-8 flex items-center gap-3 pt-5" data-id="element-362" data-mp="3da0miwtf8a46">
              <div className="h-1 w-40 overflow-hidden rounded-full bg-ink/10" data-id="element-363" data-mp="3i44yxvlghacn">
                <div className="h-full rounded-full bg-brick transition-[width] duration-200 ease-republic" style={{
                width: `${(step + 1) / total * 100}%`
              }} data-id="element-364" data-mp="33eyyy5esng3p" />
              </div>
              <p className="text-sm text-ink-muted" data-id="element-365" data-mp="2uf2fci79h7fi">
                Step {step + 1} of {total}
              </p>
            </div>
          </div>

          <div data-id="element-366" data-mp="2fph9c3n3ix92">
            {step === 0 && <Field label="What are you hoping to change?" data-id="element-367" data-mp="2fsd6hpr4dhwg">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-368" data-mp="2as7lgsfcutnc">
                  {categories.map(category => <Choice key={category.slug} selected={selected.includes(category.slug)} onClick={() => toggle(category.slug)} data-id="element-369" data-mp="2orqlexy0iyok">
                      <span className="block font-medium text-inherit" data-id="element-370" data-mp="2yna94x2far28">{category.name}</span>
                      <span className="mt-1 block text-xs opacity-70" data-id="element-371" data-mp="2tps1da056hvt">{category.tagline}</span>
                    </Choice>)}
                </div>
                <p className="mt-4 text-sm text-ink-muted" data-id="element-372" data-mp="33flc68sgmsz0">Pick as many as you like.</p>
              </Field>}

            {step === 1 && <Field label="What matters most right now?" data-id="element-373" data-mp="3hd9w9eepdw30">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-374" data-mp="3chukcs09aa98">
                  {goals.map(item => <Choice key={item} selected={goal === item} onClick={() => setGoal(item)} data-id="element-375" data-mp="2a4ey4j2ldk71">
                      {item}
                    </Choice>)}
                </div>
              </Field>}

            {step === 2 && <Field label="Where should we send your lab order?" data-id="element-376" data-mp="2nua0uzaufacb">
                <label htmlFor="email" className="sr-only" data-id="element-377" data-mp="2nwmlxlbwe0tl">
                  Email address
                </label>
                <input id="email" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@email.com" className="w-full rounded-brand border border-ink/15 bg-cream-light px-5 py-4 text-base text-ink placeholder:text-ink-muted focus:border-ink" data-id="element-378" data-mp="2xspnoq2u7xg0" />
                <p className="mt-4 text-sm leading-relaxed text-ink-muted" data-id="element-379" data-mp="1cf3dnejgn7o6">
                  {text('Republic does not sell your data, full stop. Everything is stored in a HIPAA-compliant system and you can download all of it whenever you want.')}
                </p>
              </Field>}

            <div className="rule mt-10 flex items-center justify-between pt-6" data-id="element-380" data-mp="v6u25t15nxgp">
              <button type="button" onClick={() => setStep(value => Math.max(0, value - 1))} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink disabled:opacity-40" data-id="element-381" data-mp="r5gy9ohmi4cf">
                <ArrowLeftIcon className="h-4 w-4" data-id="element-382" data-mp="1ew4jfbgjuqgx" />
                Back
              </button>
              <Button size="lg" disabled={!canAdvance} onClick={() => step === total - 1 ? setSubmitted(true) : setStep(step + 1)} data-id="element-383" data-mp="19x1ers4hem9h">
                {step === total - 1 ? 'Send it over' : 'Continue'}
                <ArrowRightIcon className="h-4 w-4" data-id="element-384" data-mp="1ygyjv0tqimil" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </main>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-385" data-mp="1om6kpski7rlp">
      <h2 className="font-serif text-2xl font-semibold leading-snug text-ink md:text-3xl" data-id="element-386" data-mp="1tix0eesecl6l">
        {label}
      </h2>
      <div className="mt-6" data-id="element-387" data-mp="2h7zexdrqhxwv">{children}</div>
    </div>;
}
function Choice({
  selected,
  onClick,
  children
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`flex items-start justify-between gap-3 rounded-brand px-5 py-4 text-left text-sm transition-colors duration-150 ease-republic ${selected ? 'bg-ink text-cream' : 'border border-ink/15 text-ink-soft hover:border-ink hover:text-ink'}`} data-id="element-388" data-mp="27jhhn6iny8xz">
      <span data-id="element-389" data-mp="tiuuqvjadjpt">{children}</span>
      {selected && <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.25} data-id="element-390" data-mp="thtgyjavyj4e" />}
    </button>;
}